import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  forwardRef,
} from "react";
import PropTypes from "prop-types";

const Dropdown = forwardRef(
  (
    {
      label,
      options,
      selected,
      onChange,
      error,
      isOptionsLoading,
      multiple,
      disabled,
      placeholder = "Select an option",
      required = false,
      helperText,
      id,
      className = "",
      noOptionsMessage = "No options available",
      maxHeight = "16rem", // Allow customizing dropdown height
      enableCreateOption = false, // New feature
      onCreateOption = null, // New feature
      clearable = false, // New feature
      searchable = true, // Make search optional
      size = "medium", // Support different sizes
    },
    ref
  ) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [filter, setFilter] = useState("");

    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
    const isScrollbarClick = useRef(null);
    const componentId =
      id || `dropdown-${Math.random().toString(36).slice(2, 9)}`;
    const errorId = `${componentId}-error`;
    const helperId = `${componentId}-helper`;

    // Expose methods to parent through ref
    useEffect(() => {
      if (ref) {
        ref.current = {
          open: () => setDropdownOpen(true),
          close: () => setDropdownOpen(false),
          toggle: () => setDropdownOpen((prev) => !prev),
          clear: () => onChange(multiple ? [] : null),
          getValue: () => selected,
          setFilter: (value) => setFilter(value),
        };
      }
    }, [ref, onChange, multiple, selected]);

    const toggleDropdown = useCallback(
      (e) => {
        if (e) e.preventDefault();
        if (!disabled) {
          const newState = !dropdownOpen;
          setDropdownOpen(newState);

          // If opening, focus search input after a brief delay
          if (newState && searchable) {
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 10);
          }
        }
      },
      [disabled, dropdownOpen, searchable]
    );

    const handleOptionClick = useCallback(
      (option, e) => {
        if (e) e.stopPropagation();
        if (disabled) return;

        if (multiple) {
          // Initialize as empty array if selected is null/undefined
          const currentSelected = selected || [];
          const newValue = currentSelected.some?.(
            (item) => item._id === option._id
          )
            ? currentSelected.filter((item) => item._id !== option._id)
            : [...currentSelected, option];
          onChange(newValue);
        } else {
          onChange(option);
          setDropdownOpen(false);
          // Only clear filter after dropdown is closed to prevent flashing
          setTimeout(() => setFilter(""), 50);
        }
      },
      [multiple, onChange, selected, disabled]
    );

    const isSelected = useCallback(
      (option) => {
        if (!selected) return false;

        return multiple
          ? selected.some((item) => item._id === option._id)
          : selected._id === option._id;
      },
      [multiple, selected]
    );

    const handleClear = useCallback(
      (e) => {
        e.stopPropagation();
        onChange(multiple ? [] : null);
        setFilter("");
      },
      [onChange, multiple]
    );

    const handleCreateOption = useCallback(() => {
      if (enableCreateOption && onCreateOption && filter.trim()) {
        onCreateOption(filter.trim());
        setFilter("");
      }
    }, [enableCreateOption, onCreateOption, filter]);

    const filteredOptions = useMemo(() => {
      if (!options || options.length === 0) return [];

      return filter
        ? options.filter((option) =>
            option.name.toLowerCase().includes(filter.toLowerCase())
          )
        : options;
    }, [filter, options]);

    const handleClickOutside = useCallback((event) => {
      // Check if click was on the scrollbar by comparing clientX with the dropdown width
      if (isScrollbarClick.current) {
        isScrollbarClick.current = false;
        return;
      }

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
        setFilter("");
      }
    }, []);

    // Track currently focused option index for keyboard navigation
    const [focusedIndex, setFocusedIndex] = useState(-1);

    // Reset focused index when filtered options change
    useEffect(() => {
      setFocusedIndex(-1);
    }, [filteredOptions]);

    // Handle keyboard navigation
    const handleKeyDown = useCallback(
      (e) => {
        // Close on escape
        if (e.key === "Escape") {
          setDropdownOpen(false);
          setFilter("");
          return;
        }

        // Toggle on space or enter when focused on the main component
        if (
          (e.key === " " || e.key === "Enter") &&
          e.target === dropdownRef.current
        ) {
          e.preventDefault();
          toggleDropdown(e);
          return;
        }

        // Create option on enter when typing and no options match
        if (
          e.key === "Enter" &&
          dropdownOpen &&
          enableCreateOption &&
          filter.trim() &&
          filteredOptions.length === 0
        ) {
          handleCreateOption();
          return;
        }

        if (!dropdownOpen) return;

        // Handle arrow key navigation
        switch (e.key) {
          case "ArrowDown":
            e.preventDefault();
            setFocusedIndex((prev) =>
              prev < filteredOptions.length - 1 ? prev + 1 : 0
            );
            break;

          case "ArrowUp":
            e.preventDefault();
            setFocusedIndex((prev) =>
              prev > 0 ? prev - 1 : filteredOptions.length - 1
            );
            break;

          case "Enter":
            e.preventDefault();
            if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
              handleOptionClick(filteredOptions[focusedIndex]);
            }
            break;

          case "Tab":
            setDropdownOpen(false);
            break;

          default:
            break;
        }
      },
      [
        dropdownOpen,
        toggleDropdown,
        enableCreateOption,
        filter,
        filteredOptions,
        focusedIndex,
        handleCreateOption,
        handleOptionClick,
      ]
    );

    useEffect(() => {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [handleClickOutside]);

    // Reset filter and focused index when dropdown closes
    useEffect(() => {
      if (!dropdownOpen) {
        setFilter("");
        setFocusedIndex(-1);
      }
    }, [dropdownOpen]);

    // Determine display value for the dropdown
    const displayValue = useMemo(() => {
      if (!selected || (multiple && selected.length === 0)) {
        return placeholder;
      }

      if (multiple) {
        return selected.length > 2
          ? `${selected
              .slice(0, 2)
              .map((item) => item.name)
              .join(", ")} + ${selected.length - 2} more`
          : selected.map((item) => item.name).join(", ");
      }

      return selected.name;
    }, [selected, multiple, placeholder]);

    const getSizeClasses = () => {
      switch (size) {
        case "small":
          return "px-2 py-1 text-xs";
        case "large":
          return "px-4 py-3 text-base";
        default: // medium
          return "px-3 py-2 text-sm";
      }
    };

    return (
      <div className={`relative w-full ${className}`} ref={dropdownRef}>
        {label && (
          <div className="flex items-center justify-between mb-1.5">
            <label
              htmlFor={componentId}
              className={`block text-sm font-medium ${disabled ? "text-neutral-dark/60" : "text-text"}`}
            >
              {label}
              {required && <span className="ml-1 text-danger">*</span>}
            </label>

            {helperText && (
              <span id={helperId} className="text-xs text-neutral-dark">
                {helperText}
              </span>
            )}
          </div>
        )}

        <div
          id={componentId}
          className={`relative w-full bg-background border ${
            error
              ? "border-danger"
              : disabled
                ? "border-neutral-light"
                : "border-neutral-dark"
          } rounded-lg ${!disabled && "hover:border-primary"} ${
            dropdownOpen && !disabled
              ? "ring-2 ring-primary-light border-primary"
              : ""
          } transition-all duration-200 ${disabled ? "opacity-70" : ""}`}
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={dropdownOpen}
          aria-disabled={disabled}
          aria-controls={`${componentId}-options`}
          aria-labelledby={`${componentId}-label`}
          aria-describedby={`${error ? errorId : ""} ${helperText ? helperId : ""}`}
          tabIndex={disabled ? -1 : 0}
        >
          <div
            className={`flex justify-between items-center ${getSizeClasses()} ${
              disabled
                ? "cursor-not-allowed bg-neutral-light"
                : "cursor-pointer"
            }`}
          >
            <span
              className={`block truncate ${
                !selected || (multiple && selected.length === 0)
                  ? "text-neutral-dark/60"
                  : "text-text"
              }`}
            >
              {displayValue}
            </span>

            <div className="flex items-center gap-1">
              {clearable &&
                selected &&
                (!multiple || selected.length > 0) &&
                !disabled && (
                  <button
                    type="button"
                    className="flex-shrink-0 text-neutral-dark hover:text-danger p-1 rounded-full hover:bg-neutral-light transition-colors"
                    onClick={handleClear}
                    aria-label="Clear selection"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                )}

              <span
                className={`pointer-events-none flex-shrink-0 transition-transform duration-200 ${
                  dropdownOpen ? "transform rotate-180" : ""
                }`}
              >
                <svg
                  className={`h-5 w-5 ${disabled ? "text-neutral-dark/60" : "text-primary"}`}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            </div>
          </div>

          {dropdownOpen && !disabled && (
            <div
              className="absolute inset-x-0 mt-1 w-full rounded-md bg-background border border-neutral-light shadow-lg z-20 overflow-hidden"
              style={{ width: "100%" }}
            >
              {searchable && (
                <div className="sticky top-0 bg-background p-2 border-b border-neutral-light z-10">
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="block w-full px-3 py-2 text-sm border border-neutral-light rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary"
                    placeholder="Search..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.key === "Escape" && e.stopPropagation()}
                    aria-label="Search options"
                    aria-controls={`${componentId}-options`}
                  />
                </div>
              )}

              <ul
                id={`${componentId}-options`}
                className="max-h-64 py-1 overflow-y-auto scrollbar-thin scrollbar-thumb-neutral-light scrollbar-track-transparent"
                onMouseDown={(e) => {
                  // Detect clicks on the scrollbar by checking if the click position is near the edge
                  const rect = e.currentTarget.getBoundingClientRect();
                  const isScrollbarArea = e.clientX > rect.right - 20; // 20px threshold for scrollbar
                  if (isScrollbarArea) {
                    isScrollbarClick.current = true;
                  }
                }}
                style={{ maxHeight: maxHeight }}
                role="listbox"
                aria-multiselectable={multiple}
              >
                {isOptionsLoading ? (
                  <li className="px-4 py-2 text-center text-neutral-dark">
                    <div className="flex items-center justify-center">
                      <svg
                        className="animate-spin h-5 w-5 text-primary"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span className="ml-2">Loading...</span>
                    </div>
                  </li>
                ) : filteredOptions.length === 0 ? (
                  <li className="px-4 py-3 text-neutral-dark text-center">
                    {enableCreateOption ? (
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-primary hover:bg-primary-light/10 rounded transition-colors"
                        onClick={handleCreateOption}
                      >
                        <span className="font-medium">Create:</span> "{filter}"
                      </button>
                    ) : (
                      noOptionsMessage
                    )}
                  </li>
                ) : (
                  filteredOptions.map((option) => {
                    const optionSelected = isSelected(option);
                    return (
                      <li
                        key={option._id}
                        className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                          optionSelected
                            ? "text-text-on-primary bg-primary"
                            : "text-text hover:bg-primary-light/10"
                        } ${
                          filteredOptions.indexOf(option) === focusedIndex
                            ? "ring-2 ring-primary-light"
                            : ""
                        }`}
                        onClick={(e) => handleOptionClick(option, e)}
                        role="option"
                        aria-selected={optionSelected}
                      >
                        <div className="flex items-center">
                          <span
                            className={`block truncate ${
                              optionSelected ? "font-semibold" : "font-normal"
                            }`}
                          >
                            {option.name}
                          </span>
                        </div>

                        {optionSelected && (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-3">
                            <svg
                              className="h-5 w-5"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                        )}
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          )}
        </div>

        {error && (
          <div id={errorId} className="text-danger text-sm mt-1.5" role="alert">
            {error}
          </div>
        )}
      </div>
    );
  }
);

Dropdown.displayName = "Dropdown";

Dropdown.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  selected: PropTypes.oneOfType([
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      })
    ),
    PropTypes.oneOf([null]), // Allow null for unselected state
  ]),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  isOptionsLoading: PropTypes.bool,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  helperText: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  noOptionsMessage: PropTypes.string,
  maxHeight: PropTypes.string,
  enableCreateOption: PropTypes.bool,
  onCreateOption: PropTypes.func,
  clearable: PropTypes.bool,
  searchable: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium", "large"]),
};

Dropdown.defaultProps = {
  error: "",
  isOptionsLoading: false,
  multiple: false,
  disabled: false,
  placeholder: "Select an option",
  required: false,
  noOptionsMessage: "No options available",
  maxHeight: "16rem",
  enableCreateOption: false,
  clearable: false,
  searchable: true,
  size: "medium",
};

export default React.memo(Dropdown);
